import { http, HttpResponse } from 'msw';

import { env } from '@/config/env';
import { axios } from '@/lib/axios';
import { networkDelay } from '@/test/mocks/utils';

export interface ChatInteraction {
  question: string;
  answer: string;
  version: number;
  timestamp: string;
  session_uuid: string;
  options: string[];
  user_device: string;
  positive_feedback?: boolean;
  question_by_bb?: string;
  answer_by_student?: string;
}

export interface ChatHistoryResponse {
  interactions: ChatInteraction[];
  available_chat_count: number;
}

export interface ChatHistoryParams {
  lead_id: string;
  source: string;
}

export const getChatHistory = (
  params: ChatHistoryParams
): Promise<ChatHistoryResponse> => {
  return axios.get('/doubtgpt/v1/interaction_history', { params });
};

export const getChatHistoryMockHandler = http.get(
  `${env.API_URL}/doubtgpt/v1/interaction_history`,
  async ({ request }): Promise<HttpResponse<ChatHistoryResponse>> => {
    const url = new URL(request.url);
    // Extract parameters for potential future use
    url.searchParams.get('lead_id');
    url.searchParams.get('source');

    await networkDelay();

    // Enhanced mock response with diverse and engaging chat messages
    const mockResponse: ChatHistoryResponse = {
      interactions: [
        {
          question: 'Hi! Can you help me understand algebra?',
          answer:
            "Hello! 😊 I'd be absolutely delighted to help you with algebra! 🧮</br></br>Algebra is like a puzzle where we use letters (variables) to represent numbers we don't know yet. Think of it as a detective game where we're trying to find the missing pieces!</br></br>For example, if we have: <b>2x + 3 = 11</b></br></br>We need to find what <b>x</b> equals. Let's solve it step by step:</br>• Subtract 3 from both sides: <b>2x = 8</b></br>• Divide both sides by 2: <b>x = 4</b></br></br>So <b>x = 4</b>! 🎯</br></br>Would you like to practice some more examples?",
          version: 1_744_439_172,
          timestamp: '1744439172',
          session_uuid: '2025-04-12-3462916eaa',
          options: ["Yes, let's practice!", 'Can you explain more?'],
          user_device: 'desktop',
          positive_feedback: true,
        },
        {
          question: 'What is the quadratic formula and how do I use it?',
          answer:
            "Great question! 🎓 The quadratic formula is one of the most powerful tools in algebra!</br></br><b>Quadratic Formula:</b></br><b>x = (-b ± √(b² - 4ac)) / 2a</b></br></br>This formula solves any quadratic equation in the form: <b>ax² + bx + c = 0</b></br></br><b>Example:</b> Solve <b>x² + 5x + 6 = 0</b></br></br>Here, <b>a = 1</b>, <b>b = 5</b>, <b>c = 6</b></br></br>Plugging into the formula:</br><b>x = (-5 ± √(25 - 24)) / 2</b></br><b>x = (-5 ± √1) / 2</b></br><b>x = (-5 ± 1) / 2</b></br></br>So <b>x = -2</b> or <b>x = -3</b>! 🎯</br></br>The ± symbol means we get two answers - that's why quadratics often have two solutions!",
          version: 1_746_871_781,
          timestamp: '1746871781',
          session_uuid: '2025-05-10-e77e7c7fd3',
          options: ['Show me another example', 'What about complex numbers?'],
          user_device: 'desktop',
          positive_feedback: false,
        },
        {
          question: 'Can you explain calculus in simple terms?',
          answer:
            "Absolutely! 🧠 Calculus is like the 'mathematics of change' - it helps us understand how things change over time!</br></br><b>Two main branches:</b></br></br>1️⃣ <b>Differential Calculus</b> - Studies rates of change (like speed, acceleration)</br>2️⃣ <b>Integral Calculus</b> - Studies accumulation (like area, volume)</br></br><b>Real-world example:</b> Imagine you're driving a car:</br>• <b>Position</b> = where you are</br>• <b>Velocity</b> = how fast you're going (derivative of position)</br>• <b>Acceleration</b> = how quickly you're speeding up/slowing down (derivative of velocity)</br></br>Calculus connects all these concepts! It's like having a mathematical superpower to understand the world around us! 🌟</br></br>Would you like to explore any specific calculus concept?",
          version: 1_747_123_456,
          timestamp: '1747123456',
          session_uuid: '2025-05-12-abc123def',
          options: ['Tell me about derivatives', 'What about integrals?'],
          user_device: 'desktop',
        },
        {
          question: "I'm struggling with geometry proofs. Any tips?",
          answer:
            "Geometry proofs can be tricky at first! 📐 But don't worry - I have some great strategies to help you master them!</br></br><b>Key Tips for Geometry Proofs:</b></br></br>1️⃣ <b>Start with what you know</b> - List all given information</br>2️⃣ <b>Draw a diagram</b> - Visual representation helps a lot!</br>3️⃣ <b>Use definitions</b> - Know your geometric terms</br>4️⃣ <b>Work backwards</b> - Sometimes starting from the conclusion helps</br></br><b>Example:</b> Prove that the sum of angles in a triangle is 180°</br></br>• Draw a triangle ABC</br>• Draw a line parallel to BC through A</br>• Use alternate interior angles</br>• Show that ∠A + ∠B + ∠C = 180°</br></br>Practice makes perfect! Would you like to work through a specific proof together? 🎯",
          version: 1_747_234_567,
          timestamp: '1747234567',
          session_uuid: '2025-05-13-def456ghi',
          options: ['Show me a proof example', 'What about circle theorems?'],
          user_device: 'desktop',
        },
        {
          question: '20',
          answer:
            '\u2705 Correct! 25% of 80 is indeed 20. Well done! \uD83C\uDF89',
          version: 1_756_806_710,
          timestamp: '1756806710',
          session_uuid: '2025-09-02-8a1c31a495',
          options: [],
          user_device: 'desktop',
          question_by_bb: "What's 25% of 80? \uD83E\uDDE0",
          answer_by_student: '20',
        },
      ],
      available_chat_count: 20,
    };

    return HttpResponse.json(mockResponse);
  }
);
